import React, { Fragment, useEffect, useState } from 'react';

import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';
import {
  Auftrag,
  Auftragsposition,
  Lademittel,
  TrackAndTrace,
} from '../../api';
import { utcIsoDateToLocaleDate } from '../../util/util';
import { StateModel } from '../../redux/store';
import { ConfigModel } from '../../models/ConfigModel';

interface AuftragInfoProps {
  auftrag: Auftrag;
  TT: TrackAndTrace;
  kunde: string;
  config: ConfigModel;
}

/**
 * AuftragSidebarTab-Component
 *
 * Dient zur Anzeige von Auftragsinformationen
 *
 * @returns {JSX.Element} AuftragSidebarTab-Component
 * @constructor
 */
const AuftragInfo = (props: AuftragInfoProps) => {
  const [selectedAuftrag, setSelectedAuftrag] = useState<Auftrag>();
  const [anzahlVerpackungen, setAnzahlVerpackungen] = useState(0);
  const [lademittel, setLademittel] = useState({});
  const [bruttogewicht, setBruttogewicht] = useState(0.0);
  const [lademeter, setLademeter] = useState(0.0);
  const [kubikmeter, setKubikmeter] = useState(0.0);

  useEffect(() => {
    setSelectedAuftrag(props.auftrag);
    updateLocalState();
  }, [props.auftrag]);

  const updateLocalState = () => {
    let verpackungAnzahl = 0;
    let tmpLademittel = {};
    let bruttogewicht = 0.0;
    let lademeter = 0.0;
    let kubikmeter = 0.0;

    props.auftrag?.auftragspositionen?.map((auftragPos: Auftragsposition) => {
      if (auftragPos.verpackungAnzahl) {
        verpackungAnzahl += auftragPos.verpackungAnzahl;
      }
      if (auftragPos.bruttoGewicht) {
        bruttogewicht += auftragPos.bruttoGewicht;
      }
      if (auftragPos.lademeter) {
        lademeter += auftragPos.lademeter;
      }
      if (auftragPos.volumenCBM) {
        kubikmeter += auftragPos.volumenCBM;
      }

      if (auftragPos.lademittel && auftragPos.lademittelAnzahl) {
        tmpLademittel = addLademittel(
          tmpLademittel,
          auftragPos.lademittel,
          auftragPos.lademittelAnzahl
        );
      }
    });
    setAnzahlVerpackungen(verpackungAnzahl);
    setLademittel(tmpLademittel);
    setBruttogewicht(bruttogewicht);
    setLademeter(lademeter);
    setKubikmeter(kubikmeter);
  };

  const addLademittel = (
    tmpLademittel: any,
    lademittelObject: Lademittel,
    lademittelAnzahl: number
  ) => {
    if (!isNaN(lademittelAnzahl) && lademittelObject.langBezeichnung) {
      if (tmpLademittel.hasOwnProperty(lademittelObject.langBezeichnung)) {
        tmpLademittel[lademittelObject.langBezeichnung] += lademittelAnzahl;
      } else {
        tmpLademittel[lademittelObject.langBezeichnung] = lademittelAnzahl;
      }
    }
    return tmpLademittel;
  };

  const getListOfAllInhalt = (): string[] => {
    const inhalt: string[] = [];
    props.auftrag?.auftragspositionen?.map((auftragpos: Auftragsposition) => {
      if (auftragpos.warengutBezeichnung != null) {
        inhalt.push(auftragpos.warengutBezeichnung);
      }
    });

    return inhalt;
  };

  const getFremdnummer = () => {
    return props.auftrag?.spedfremdnr
      ? (props.config.versenderFremdnummerLabel !== undefined
          ? props.config.versenderFremdnummerLabel
          : 'Fremdnummer: ') + props.auftrag?.spedfremdnr
      : '';
  };

  const getVersenderBlockLabel = () => {
    return props.config.versenderBlockLabel
      ? props.config.versenderBlockLabel
      : 'Versender';
  };

  return (
    <Fragment>
      <div className={'info-content'}>
        <div className={'info-block'}>
          <p>
            Auftragsnummer: <strong>{props.auftrag?.spedeigennr ?? ''}</strong>
          </p>
          {props.kunde == 'MMT' && (
            <p>
              AB-Nr.:
              <strong>{props.TT.abnr}</strong>
            </p>
          )}
          {props.kunde == 'BEXITY' && (
            <Fragment>
              <p>
                Referenz:
                <strong>{props.auftrag?.spedfremdnr}</strong>
              </p>
              <p>
                Partnerreferenz:
                <strong>{props.auftrag?.partnerReferenz}</strong>
              </p>
            </Fragment>
          )}
        </div>
      </div>

      <div className={'info-content'}>
        {/*Versender*/}
        <div className={'info-block'}>
          <label className={'info-label'}>{getVersenderBlockLabel()}</label>
          {props.config.versenderMatchcode && (
            <p>{props.auftrag?.absenderAdresse?.matchcode ?? ''}</p>
          )}
          {props.config.versenderFremdnummer && <p>{getFremdnummer()}</p>}
          <p>
            {props.config.versenderName &&
              (props.auftrag?.absenderAdresse?.name ?? '')}
          </p>
          {props.config.versenderStrasse && (
            <p>
              {props.auftrag?.absenderAdresse?.strasse ?? ''}{' '}
              {props.auftrag?.absenderAdresse?.hausnummer ?? ''}
            </p>
          )}
          {props.config.versenderOrt && (
            <p>
              {props.auftrag?.absenderAdresse?.plz ?? ''}{' '}
              {props.auftrag?.absenderAdresse?.ort ?? ''}
            </p>
          )}
        </div>
        {/*Todo Versender Kontakt + Telefonnummer oder E-Mail Adresse*/}

        {/*Empfaenger*/}
        <div className={'info-block'}>
          <label className={'info-label'}>{'Empfänger'}</label>
          <p>
            {(props.config.empfaengerMatchcode &&
              props.auftrag?.empfaengerAdresse?.matchcode) ??
              ''}
            {props.config.empfaengerFremdnummer &&
              (props.auftrag?.empfaengerAdresse?.fremdnummer
                ? ', ' + props.auftrag?.empfaengerAdresse?.fremdnummer
                : '')}
          </p>
          <p>{props.auftrag?.empfaengerAdresse?.name ?? ''}</p>
          {props.config.empfaengerStrasse && (
            <p>
              {props.auftrag?.empfaengerAdresse?.strasse ?? ''}{' '}
              {props.auftrag?.empfaengerAdresse?.hausnummer ?? ''}
            </p>
          )}
          <p>
            {props.auftrag?.empfaengerAdresse?.plz ?? ''}{' '}
            {props.auftrag?.empfaengerAdresse?.ort ?? ''}
          </p>
          {/*Todo Empfaenger Kontakt + Telefonnummer oder E-Mail Adresse*/}
        </div>

        {/*Versanddaten*/}
        {selectedAuftrag?.terminkennzeichen?.langBezeichnung ||
        selectedAuftrag?.abholDatumVon ||
        selectedAuftrag?.anlieferkennzeichen?.langBezeichnung ||
        selectedAuftrag?.zustellDatumVon ||
        selectedAuftrag?.zustellkennzeichen?.langBezeichnung ? (
          <div className={'info-block'}>
            <label className={'info-label'}>{'Versanddaten'}</label>
            <p>
              {selectedAuftrag?.terminkennzeichen
                ? 'Terminart: ' +
                  selectedAuftrag.terminkennzeichen.langBezeichnung
                : ''}
            </p>
            <p>
              {selectedAuftrag?.abholDatumVon
                ? 'Versanddatum: ' +
                  utcIsoDateToLocaleDate(selectedAuftrag.abholDatumVon) +
                  ' '
                : ''}
            </p>
            <p>
              {selectedAuftrag?.anlieferkennzeichen
                ? 'Anlieferart: ' +
                  selectedAuftrag.anlieferkennzeichen.langBezeichnung
                : ''}
            </p>
            <p>
              {selectedAuftrag?.zustellDatumVon
                ? 'Zustelldatum: ' +
                  utcIsoDateToLocaleDate(selectedAuftrag.zustellDatumVon)
                : ''}
            </p>
            <p>
              {selectedAuftrag?.zustellkennzeichen
                ? 'Zustellart: ' +
                  selectedAuftrag.zustellkennzeichen.langBezeichnung
                : ''}
            </p>
          </div>
        ) : (
          ''
        )}

        {/*Senungspos, Lademittel etc*/}
        {props.config.inhalt && (
          <div className={'info-block'}>
            <label className={'info-label'}>{'Inhalt'}</label>
            {getListOfAllInhalt().map((value: string, index: number) => {
              return <p key={index}>{value}</p>;
            })}
          </div>
        )}
        <div className={'info-block'}>
          <label className={'info-label'}>{'Übersicht'}</label>
          <p>
            {anzahlVerpackungen} {'Verpackungen'}
          </p>
          {Object.entries(lademittel).map(([key, value]) => (
            <p key={key}>
              {value} {key}
            </p>
          ))}
          <NumberFormat
            value={bruttogewicht}
            displayType={'text'}
            thousandSeparator={'.'}
            decimalSeparator={','}
            suffix={' kg'}
          />
          <br />
          <NumberFormat
            value={lademeter}
            displayType={'text'}
            thousandSeparator={'.'}
            decimalSeparator={','}
            suffix={' LDM'}
          />
          <br />
          <NumberFormat
            value={kubikmeter}
            displayType={'text'}
            thousandSeparator={'.'}
            decimalSeparator={','}
            suffix={' cbm'}
          />
          <br />
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state: StateModel) => ({
  loading: state.Loading.loading,
  TT: state.TT,
  kunde: state.Config.clientKunde?.toUpperCase() ?? 'WDS',
  config: state.Config,
});
export default connect(mapStateToProps, {})(AuftragInfo);
